// Light/dark theme toggle. Applies via [data-theme] attribute on <html>.
// CSS variables in styles.css respond to this attribute.
// Persists choice to localStorage; falls back to OS preference on first visit.

const LS_KEY = 'hd:v2:theme';
const VALID = new Set(['dark', 'light']);
const listeners = new Set();

let current = (() => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && VALID.has(saved)) return saved;
  } catch {}
  // OS preference, default dark for our brand.
  try {
    if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
  } catch {}
  return 'dark';
})();

export function getTheme() { return current; }

export function setTheme(name) {
  if (!VALID.has(name) || name === current) return;
  current = name;
  applyTheme();
  try { localStorage.setItem(LS_KEY, name); } catch {}
  listeners.forEach((fn) => { try { fn(name); } catch {} });
}

export function toggleTheme() {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

export function onThemeChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Applies the current theme to <html data-theme="..."> */
export function applyTheme() {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', current);
}

/** Mount a small icon toggle inside `container`. */
export function mountThemeToggle(container) {
  if (!container) return;
  const btn = document.createElement('button');
  btn.id = 'theme-toggle';
  btn.type = 'button';
  btn.title = 'Toggle light / dark';
  btn.setAttribute('aria-label', 'Toggle light / dark theme');
  btn.style.cssText = [
    'display:flex', 'align-items:center', 'justify-content:center',
    'width:26px', 'height:26px', 'padding:0',
    'background:transparent', 'border:1px solid var(--bd2)',
    'border-radius:4px', 'cursor:pointer',
    'color:var(--tx2)', 'transition:.15s',
  ].join(';');
  // Icon swaps with theme — sun in dark mode (click to lighten), moon in light.
  const render = () => {
    btn.innerHTML = current === 'dark' ? sunSvg() : moonSvg();
  };
  btn.addEventListener('click', toggleTheme);
  btn.addEventListener('mouseover', () => { btn.style.borderColor = 'var(--tx2)'; btn.style.color = 'var(--tx)'; });
  btn.addEventListener('mouseout',  () => { btn.style.borderColor = 'var(--bd2)'; btn.style.color = 'var(--tx2)'; });
  container.appendChild(btn);
  render();
  onThemeChange(render);
}

function sunSvg() {
  return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>`;
}
function moonSvg() {
  return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
}
